import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const TerminologyContext = createContext(null);

/**
 * TerminologyProvider
 * Fetches resolved terminology from /api/admin/terminology after login.
 * Provides t(key, opts) helper that returns the effective label for a canonical key.
 * Falls back gracefully to title-cased key if not loaded.
 * Platform Admin users (scope=PLATFORM) get no terminology — they use system defaults only.
 */
export function TerminologyProvider({ children }) {
  const { user } = useAuth();
  // map: canonical_key → full resolved term object
  const [terminology, setTerminology] = useState({});
  const [loaded, setLoaded] = useState(false);

  const loadTerminology = useCallback(async () => {
    if (!user || user.scope === 'PLATFORM' || user.role === 'platform_admin') {
      setLoaded(true);
      return;
    }
    try {
      const r = await api.get('/admin/terminology');
      const map = {};
      for (const item of r.data.items || []) {
        map[item.canonical_key] = item;
      }
      setTerminology(map);
    } catch {
      // Non-fatal — UI falls back to key-derived labels
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadTerminology();
    } else {
      setTerminology({});
      setLoaded(false);
    }
  }, [user, loadTerminology]);

  /**
   * t(canonicalKey, opts?) — resolve a label for a canonical key.
   * opts.plural → return plural label
   * opts.menu   → return menu_label
   * opts.short  → return short_label
   * Default: singular_label
   */
  const t = useCallback((canonicalKey, opts = {}) => {
    const item = terminology[canonicalKey];
    if (!item) {
      // Graceful fallback: "COMMUNITY_GROUP" → "Community Group"
      return canonicalKey
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase());
    }
    if (opts.plural) return item.effective_plural || item.effective_singular;
    if (opts.menu) return item.effective_menu || item.effective_singular;
    if (opts.short) return item.effective_short || item.effective_singular;
    return item.effective_singular;
  }, [terminology]);

  return (
    <TerminologyContext.Provider value={{ terminology, t, loadTerminology, loaded }}>
      {children}
    </TerminologyContext.Provider>
  );
}

export function useTerminology() {
  const ctx = useContext(TerminologyContext);
  if (!ctx) throw new Error('useTerminology must be used within TerminologyProvider');
  return ctx;
}
